import type {
  AIActivity,
  AICustomerProfile,
  AIFollowUp,
  AILead,
  AIConversation,
  IntelligenceOverview,
} from '../types/contracts';
import {
  mockConversations,
  mockFollowUps,
  mockLeads,
  mockOverview,
  mockProfile,
  mockTimeline,
} from '../mocks/intelligence.mock';

export type Result<T> = { ok: true; data: T } | { ok: false; message: string };

/**
 * Data contract for the Intelligence module.
 * Sprint 2 backend only needs to provide an implementation of this interface.
 */
export interface IntelligenceAdapter {
  getOverview(): Promise<Result<IntelligenceOverview>>;
  listConversations(): Promise<Result<AIConversation[]>>;
  getConversation(id: string): Promise<Result<AIConversation>>;
  getCustomerProfile(customerId: string): Promise<Result<AICustomerProfile>>;
  listLeads(): Promise<Result<AILead[]>>;
  getLead(id: string): Promise<Result<AILead>>;
  getLeadTimeline(leadId: string): Promise<Result<AIActivity[]>>;
  listFollowUps(): Promise<Result<AIFollowUp[]>>;
  completeFollowUp(id: string): Promise<Result<null>>;
  rescheduleFollowUp(id: string, dueAt: string): Promise<Result<null>>;
  takeoverConversation(id: string): Promise<Result<null>>;
  resumeAi(id: string): Promise<Result<null>>;
}

const delay = (ms = 350) => new Promise((r) => setTimeout(r, ms));
const ok = <T>(data: T): Result<T> => ({ ok: true, data });

/** Mock adapter — realistic latency, no backend, no database. */
class MockIntelligenceAdapter implements IntelligenceAdapter {
  async getOverview() { await delay(); return ok(mockOverview); }
  async listConversations() { await delay(); return ok(mockConversations); }
  async getConversation(id: string) {
    await delay();
    const c = mockConversations.find((x) => x.id === id);
    return c ? ok(c) : { ok: false as const, message: 'Conversation not found' };
  }
  async getCustomerProfile(customerId: string) { await delay(); return ok(mockProfile(customerId)); }
  async listLeads() { await delay(); return ok(mockLeads); }
  async getLead(id: string) {
    await delay();
    const l = mockLeads.find((x) => x.id === id);
    return l ? ok(l) : { ok: false as const, message: 'Lead not found' };
  }
  async getLeadTimeline(leadId: string) { await delay(); return ok(mockTimeline(leadId)); }
  async listFollowUps() { await delay(); return ok(mockFollowUps); }
  async completeFollowUp(_id: string) { await delay(200); return ok(null); }
  async rescheduleFollowUp(_id: string, _dueAt: string) { await delay(200); return ok(null); }
  async takeoverConversation(_id: string) { await delay(200); return ok(null); }
  async resumeAi(_id: string) { await delay(200); return ok(null); }
}

/** Live Intelligence API adapter (Sprint 2). MockIntelligenceAdapter stays available for local previews. */
import { HttpIntelligenceAdapter } from './http-adapter';
/** Live Intelligence API adapter (Sprint 2). MockIntelligenceAdapter stays for previews. */
export const intelligenceApi: IntelligenceAdapter = new HttpIntelligenceAdapter();
export { MockIntelligenceAdapter };
